/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  type ComponentId,
  type CompoundVersionId,
  type ConfigComponentArchivedSummary,
  type ConfigQueryResults,
  type ConfigurationWithoutRequest,
  type DispatchProfile,
  type ESSBillOfMaterials,
  type ESSCostingConfiguration,
  type ESSCostingSimulationRequest,
  type ESSCostingSimulationRequestWithName,
  type FlowRunLogs,
  getElementFromPossibleList,
  type LiIonSimulationOutputs,
  type ListResultsQuery,
  type OptionalCompoundId,
  parseCsvString,
  type ScenarioProjectColumn,
  type ScenarioRecord,
  type ScenarioWorkflow,
} from '@poweralpha/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import type { HTTPError } from 'ky';
import { api, getQueryConfigsSearchParams, getResult, type QueryConfigsSearchParams } from '../api';
import { getMultiplePCSs, type PcsQueryReturn } from '../database/pcs/api';
import type { PrevResultsComparisonQuery } from '../pv/pvcosting/models';
import { objectToQueryKey } from '../queryClient';
import type { ConfigType } from '../react-components/configs/models';
import type { MutationOptions, QueryOptions } from '../react-components/queries';
import type { RefreshBomsParams } from './models';

export const generateBoms = (
  request: RefreshBomsParams | undefined,
  user: string,
): Promise<Array<ESSBillOfMaterials>> =>
  api.post('proxy/ess/costing/boms', { json: request, headers: { user } }).json();

export const useRefreshBomsQuery = (
  params: RefreshBomsParams | undefined,
  user: string | undefined,
  opts?: QueryOptions<Array<ESSBillOfMaterials>>,
) =>
  useQuery({
    queryKey: ['refresh-ess-boms', objectToQueryKey(params)],
    queryFn: () => generateBoms(params, user ?? ''),
    staleTime: Number.POSITIVE_INFINITY,
    ...opts,
    enabled: Boolean(params) && (opts?.enabled ?? true),
  });

export const useGenerateBoms = (
  user: string | undefined,
  opts?: MutationOptions<
    Array<ESSBillOfMaterials>,
    HTTPError,
    { params: RefreshBomsParams; action: 'reset' | 'sync' }
  >,
) =>
  useMutation({
    mutationFn: ({ params }) => generateBoms(params, user ?? ''),
    ...opts,
  });

export const useEssConfigMetaQuery = (
  ids: Array<[string, number]>,
  opts?: QueryOptions<ListResultsQuery<ConfigurationWithoutRequest>>,
) => {
  const stringifiedIds = ids.map(([id, version]) => `${id}_${version}`);
  return useQuery<ListResultsQuery<ConfigurationWithoutRequest>, HTTPError>({
    queryKey: ['ess-config-meta', ...ids],
    queryFn: () => api.get(`ess/multiple-configs?id=${stringifiedIds.join('&id=')}`).json(),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: ids.length ? opts?.enabled : false,
  });
};

export const usePcsQuery = (
  ids: Partial<Record<keyof PcsQueryReturn, string | undefined>>,
  opts?: QueryOptions<PcsQueryReturn>,
) =>
  useQuery<PcsQueryReturn>({
    queryKey: ['pcs-query', ...Object.values(ids)],
    queryFn: () => getMultiplePCSs(ids),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(ids?.inverter ?? ids?.mvTransformer) && (opts?.enabled ?? true),
  });

export const runESSCostingSimulation = async (
  config: ESSCostingConfiguration,
  user: string,
): Promise<ComponentId> =>
  api.post('proxy/ess/costing/simulate', { json: config, headers: { user } }).json();

export const runESSCostingQuickSim = async (
  request: ESSCostingSimulationRequest,
  user: string,
): Promise<LiIonSimulationOutputs> =>
  api.post('proxy/ess/costing/simulate', { json: request, headers: { user } }).json();

export const getESSSimulationLogs = async (): Promise<FlowRunLogs[]> => api.get('ess/logs').json();

export const getEssCostingConfigurations = async (
  queryOptions?: QueryConfigsSearchParams,
): Promise<ConfigQueryResults<ESSCostingConfiguration>> => {
  const searchParams = getQueryConfigsSearchParams(queryOptions);
  return api.get('ess/configs', { searchParams }).json();
};

export const getEssCostingConfiguration = async (
  id: string | undefined | null,
  version?: number,
): Promise<ESSCostingConfiguration> =>
  api
    .get(`ess/configs/${id}`, version !== undefined ? { searchParams: { version } } : {})
    .json<ESSCostingConfiguration>();

export const useEssConfigurationsQuery = (ids: Array<CompoundVersionId['id']> | undefined) =>
  useQuery<Array<ESSCostingConfiguration>>({
    queryKey: ['ess-configs', ...(ids ?? [])],
    queryFn: () =>
      api
        .get(
          `ess/configs/multiple?ids=${ids?.map(([id]) => id).join('&ids=')}&versions=${ids
            ?.map(([, version]) => version)
            .join('&versions=')}`,
        )
        .json(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(ids?.length),
  });

export const getEssConfigMaxVersion = async (id: string | undefined | null): Promise<number> =>
  api.get(`ess/configs/${id}/max-version`).json();

export const saveEssCostingConfiguration = async (
  request: ESSCostingSimulationRequestWithName,
): Promise<ESSCostingConfiguration> => api.post('ess/configs', { json: request }).json();

export const editEssCostingConfiguration = async (
  request: ESSCostingSimulationRequestWithName,
  id: string | null,
  version: number,
): Promise<ESSCostingConfiguration> =>
  api.post(`ess/configs/${id}/${version}`, { json: request }).json();

export const useEssConfigQuery = (
  id: OptionalCompoundId | undefined,
  opts?: QueryOptions<ESSCostingConfiguration> & {
    prefetch?: () => Promise<void>;
  },
) =>
  useQuery({
    queryKey: ['ess-config' satisfies `${ConfigType}-config`, ...(id ?? [])],
    queryFn: async () => {
      await opts?.prefetch?.();
      return getEssCostingConfiguration(id?.[0], id?.[1]);
    },
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: id?.[0] ? opts?.enabled : false,
  });

export const useEssCostingScenarioQuery = (id: string | undefined | null) =>
  useQuery({
    queryKey: ['ess-costing', id],
    queryFn: () =>
      getResult<{
        ess_costing: ScenarioRecord['ess_costing'];
        start_time: ScenarioRecord['start_time'];
        workflow: ScenarioWorkflow;
        project: ScenarioProjectColumn | null;
      }>(
        id,
        { ess_costing: true, start_time: true, workflow: true, project: true },
        'scenario_id',
      ).then((record) => ({
        result: getElementFromPossibleList(record.ess_costing),
        start_time: record.start_time,
        workflow: record.workflow,
        project: record.project,
      })),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const useQuickSimMutation = (
  user: string | undefined,
  opts?: MutationOptions<LiIonSimulationOutputs, HTTPError, ESSCostingSimulationRequest>,
) =>
  useMutation({
    mutationFn: (request) => runESSCostingQuickSim(request, user ?? ''),
    ...opts,
  });

export const usePrevResultsQuery = (
  projectId: string | undefined | null,
  configId: string | undefined | null,
  opts?: QueryOptions<PrevResultsComparisonQuery>,
) =>
  useQuery({
    queryKey: ['esscosting-prev-results', projectId, configId],
    queryFn: () =>
      api
        .get('ess/costing/results-comparison', {
          searchParams: {
            ...(projectId ? { projectId } : {}),
            ...(configId ? { configId } : {}),
          },
        })
        .json<PrevResultsComparisonQuery>(),
    staleTime: minutesToMilliseconds(5),
    ...opts,
  });

export const getArchivedEssCostingComponents = async (
  id: string | undefined | null,
): Promise<Array<ConfigComponentArchivedSummary>> =>
  api.get(`ess/configs/archived-components?configId=${id}`).json();

export const useDispatchProfilesQuery = (
  opts?: QueryOptions<{ filenames: Array<string> }, HTTPError>,
) =>
  useQuery({
    queryKey: ['dispatch-profiles'],
    queryFn: () => api.get('ess/dispatch-profiles').json<{ filenames: Array<string> }>(),
    staleTime: minutesToMilliseconds(0.5),
    ...opts,
  });

export const useDispatchProfileDataQuery = <T = DispatchProfile>(
  filename: string | undefined,
  opts?: QueryOptions<DispatchProfile, HTTPError, T>,
) =>
  useQuery({
    queryKey: ['dispatch-profiles', filename],
    queryFn: () =>
      api
        .get(`ess/dispatch-profiles/${filename}`)
        .text()
        .then((res) => parseCsvString<DispatchProfile>(res)),
    staleTime: Number.POSITIVE_INFINITY,
    ...opts,
    enabled: Boolean(filename) && (opts?.enabled ?? true),
  });

export const useAddDispatchProfileMutation = (
  opts?: MutationOptions<{ filename: string }, HTTPError, FormData>,
) =>
  useMutation({
    mutationFn: (form) => api.post(`ess/dispatch-profiles`, { body: form }).json(),
    ...opts,
  });
